import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, X } from 'lucide-react';
import {
  apiClient,
  type ApiError,
  type MembershipWithUserResponse,
  type VisibilityGrantModule,
  type VisibilityGrantResponse,
} from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import { GlassSelect } from '@/components/ui/glass-select';

/**
 * F03 granular (PR3) — secao de "Compartilhamento de visibilidade" dentro
 * do formulario de edicao de membro. Permite que o admin conceda a este
 * membro (viewer) o direito de enxergar registros de outro membro (owner)
 * em modulos especificos (Processos/Tarefas/Clientes).
 *
 * Enforcement real e 100% backend (visibility-grants module, PR1). Esta
 * tela so gerencia o CRUD das concessoes.
 */

const MODULE_LABELS: Record<VisibilityGrantModule, string> = {
  cases: 'Processos',
  tasks: 'Tarefas e Agenda',
  clients: 'Clientes',
};

const ALL_GRANT_MODULES: VisibilityGrantModule[] = ['cases', 'tasks', 'clients'];

interface VisibilityGrantsSectionProps {
  /** Membro que esta sendo editado — atua como viewer das concessoes. */
  viewer: MembershipWithUserResponse;
  /** Demais membros do tenant, para escolher como owner. */
  otherMembers: MembershipWithUserResponse[];
}

export function VisibilityGrantsSection({
  viewer,
  otherMembers,
}: VisibilityGrantsSectionProps) {
  const queryClient = useQueryClient();
  const [addOpen, setAddOpen] = useState(false);
  const [ownerUserId, setOwnerUserId] = useState<string | null>(null);
  const [modules, setModules] = useState<VisibilityGrantModule[]>([]);
  const [error, setError] = useState<string | null>(null);

  const queryKey = ['visibility-grants', viewer.userId];

  const { data: grants, isLoading } = useQuery({
    queryKey,
    queryFn: () => apiClient.listVisibilityGrants(viewer.userId),
  });

  const createMut = useMutation({
    mutationFn: () =>
      apiClient.createVisibilityGrant({
        viewerUserId: viewer.userId,
        ownerUserId: ownerUserId!,
        modules,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      setAddOpen(false);
      setOwnerUserId(null);
      setModules([]);
      setError(null);
    },
    onError: (e: ApiError) => setError(e.message),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => apiClient.deleteVisibilityGrant(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  // Agrupa concessoes por owner para exibir "nome do owner + chips de modulos".
  const grouped = new Map<string, { ownerName: string; grants: VisibilityGrantResponse[] }>();
  for (const g of grants ?? []) {
    const entry = grouped.get(g.ownerUserId);
    if (entry) {
      entry.grants.push(g);
    } else {
      grouped.set(g.ownerUserId, { ownerName: g.ownerName, grants: [g] });
    }
  }

  function toggleModule(mod: VisibilityGrantModule) {
    setModules((prev) =>
      prev.includes(mod) ? prev.filter((m) => m !== mod) : [...prev, mod],
    );
  }

  function toggleAll() {
    setModules((prev) =>
      prev.length === ALL_GRANT_MODULES.length ? [] : [...ALL_GRANT_MODULES],
    );
  }

  function submitAdd() {
    setError(null);
    if (!ownerUserId) {
      setError('Selecione o usuário que terá os registros compartilhados');
      return;
    }
    if (modules.length === 0) {
      setError('Selecione ao menos um módulo');
      return;
    }
    createMut.mutate();
  }

  const ownerOptions = otherMembers.map((m) => ({
    value: m.userId,
    label: m.user.name,
  }));

  return (
    <div className="flex flex-col gap-3 border-t border-[var(--border)] pt-4">
      <div>
        <p className="text-sm font-medium text-[var(--fg)]">
          Compartilhamento de visibilidade
        </p>
        <p className="mt-0.5 text-xs text-[var(--fg-muted)]">
          Permita que este usuário veja os registros de outros usuários.
        </p>
      </div>

      {isLoading ? (
        <div className="h-8 animate-pulse rounded bg-[color-mix(in_oklch,var(--fg)_6%,transparent)]" />
      ) : grouped.size === 0 ? (
        <p className="text-xs italic text-[var(--fg-muted)]">
          Nenhuma concessão configurada.
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {Array.from(grouped.entries()).map(([ownerUserIdKey, entry]) => (
            <li
              key={ownerUserIdKey}
              className="flex items-start justify-between gap-2 rounded-md border border-[var(--border)] bg-[color-mix(in_oklch,var(--fg)_3%,transparent)] px-3 py-2"
            >
              <div className="flex flex-col gap-1">
                <span className="text-sm text-[var(--fg)]">{entry.ownerName}</span>
                <div className="flex flex-wrap gap-1">
                  {entry.grants.map((g) => (
                    <span
                      key={g.id}
                      className="inline-flex items-center gap-1 rounded-full bg-[color-mix(in_oklch,var(--fg)_8%,transparent)] px-2 py-0.5 text-[11px] text-[var(--fg-muted)]"
                    >
                      {MODULE_LABELS[g.module]}
                      <button
                        type="button"
                        onClick={() => deleteMut.mutate(g.id)}
                        disabled={deleteMut.isPending}
                        aria-label={`Remover ${MODULE_LABELS[g.module]} de ${entry.ownerName}`}
                        className="text-[var(--fg-muted)] hover:text-red-500"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      {!addOpen ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setAddOpen(true)}
          disabled={otherMembers.length === 0}
          className="self-start"
        >
          <Plus className="mr-1.5 h-3.5 w-3.5" />
          Adicionar
        </Button>
      ) : (
        <div className="flex flex-col gap-3 rounded-md border border-[var(--border)] p-3">
          {error && (
            <p className="rounded-md bg-red-500/10 px-3 py-2 text-xs text-red-500">
              {error}
            </p>
          )}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium text-[var(--fg-muted)]">
              Ver registros de
            </label>
            <GlassSelect
              value={ownerUserId}
              onChange={setOwnerUserId}
              options={ownerOptions}
              placeholder="Selecione o usuário"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <p className="text-xs font-medium text-[var(--fg-muted)]">Módulos</p>
            <label className="flex items-center gap-2 text-sm text-[var(--fg)]">
              <input
                type="checkbox"
                checked={modules.length === ALL_GRANT_MODULES.length}
                onChange={toggleAll}
                className="h-4 w-4 rounded border-[var(--border)] accent-[var(--accent)]"
              />
              Todos
            </label>
            <div className="grid grid-cols-2 gap-1.5">
              {ALL_GRANT_MODULES.map((mod) => (
                <label key={mod} className="flex items-center gap-2 text-sm text-[var(--fg)]">
                  <input
                    type="checkbox"
                    checked={modules.includes(mod)}
                    onChange={() => toggleModule(mod)}
                    className="h-4 w-4 rounded border-[var(--border)] accent-[var(--accent)]"
                  />
                  {MODULE_LABELS[mod]}
                </label>
              ))}
            </div>
          </div>
          <div className="flex items-center justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                setAddOpen(false);
                setOwnerUserId(null);
                setModules([]);
                setError(null);
              }}
              disabled={createMut.isPending}
            >
              Cancelar
            </Button>
            <Button type="button" size="sm" onClick={submitAdd} disabled={createMut.isPending}>
              {createMut.isPending ? 'Salvando…' : 'Salvar'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
